import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MagneticButton } from '@/components/site/MagneticButton';
import { Reveal, ClipReveal } from '@/components/site/Reveal';
import { WordReveal } from '@/components/site/WordReveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { CTABand } from '@/components/site/CTABand';
import { CityStrip } from '@/components/site/CityStrip';
import { HeroScreens } from '@/components/site/HeroScreens';
import { SITE, SERVICES, WORK, STATS, CLIENTS } from '@/data/site';
import { HERO_IMAGES } from '@/data/heroImages';
import { STUDIO_IMAGE, WORK_IMAGES } from '@/data/homeImages';
import { scrollToEl } from '@/hooks/useLenis';
import { motionOn, finePointer } from '@/lib/motion';

gsap.registerPlugin(ScrollTrigger);

const WRAP = 'max-w-7xl mx-auto px-4 sm:px-6 md:px-12';
const H2 = 'font-display font-semibold text-[clamp(38px,5.6vw,92px)] leading-[0.96] tracking-[-0.045em]';
const HEADLINE = ['We', 'design', 'and', 'ship', 'digital', 'products', 'that', 'feel'];
const MASK = 'hw inline-block overflow-hidden align-top pb-[.08em] -mb-[.08em]';

const PRINCIPLES = [
  { n: 12, unit: 'Weeks', title: 'Weeks per engagement', body: 'One focused build window with a fixed scope and a fixed quote. Long enough to ship something real, short enough that nobody drifts.' },
  { n: 3, unit: 'Per quarter', title: 'Partners we say yes to', body: 'We cap the number of teams we take on, so the people you meet on the first call are the people writing your code.' },
  { n: 0, unit: 'Outsourced', title: 'Subcontracted work', body: 'Every screen and every endpoint is designed and built in-house by the twelve of us. No hand-offs to people you have never met.' },
];

const Tri = () => (
  <svg viewBox="0 0 32 32" className="h-[.38em] w-[.38em] shrink-0" aria-hidden>
    <defs>
      <linearGradient id="mq-g" x1="0" x2="1" y1="0" y2="1"><stop offset="0" stopColor="#6C5CE7" /><stop offset="1" stopColor="#4B9FFF" /></linearGradient>
    </defs>
    <path d="M16 4 L29 27 L3 27 Z" fill="url(#mq-g)" />
  </svg>
);

const HERO_SHOTS = WORK.slice(0, 3).map((w) => ({
  src: WORK_IMAGES[w.slug]?.src,
  alt: WORK_IMAGES[w.slug]?.alt || w.name,
  name: w.name,
  domain: w.domain,
}));

function Hero() {
  const root = useRef(null);
  const h1 = useRef(null);
  // Line the screenshot stack up with the first headline line; the eyebrow above
  // the h1 wraps at tablet widths, so its height is measured rather than guessed.
  useEffect(() => {
    const el = h1.current;
    const grid = el?.closest('.hero-grid');
    if (!grid || typeof ResizeObserver === 'undefined') return undefined;
    const sync = () => grid.style.setProperty('--h1-top', `${el.offsetTop}px`);
    const ro = new ResizeObserver(sync);
    ro.observe(grid);
    sync();
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    if (!motionOn()) return undefined;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.1 });
      tl.from('.hw > span', { yPercent: 115, duration: 1.3, ease: 'expo.out', stagger: 0.055 })
        .from('[data-hero-fade]', { opacity: 0, y: 24, duration: 1.1, ease: 'expo.out', stagger: 0.08 }, 0.35);
      root.current.querySelectorAll('[data-count]').forEach((el, i) => {
        const to = parseFloat(el.dataset.count); const dec = el.dataset.count.includes('.') ? 1 : 0; const o = { v: 0 };
        el.textContent = (0).toFixed(dec);
        gsap.to(o, { v: to, duration: 1.6, delay: 0.6 + i * 0.08, ease: 'power3.out', onUpdate: () => { el.textContent = o.v.toFixed(dec); } });
      });
      gsap.to('.hero-in', { yPercent: -10, opacity: 0.25, ease: 'none', scrollTrigger: { trigger: root.current, start: 'top top', end: 'bottom top', scrub: true } });
      gsap.to('.hero-screens', { yPercent: -14, ease: 'none', scrollTrigger: { trigger: root.current, start: 'top top', end: 'bottom top', scrub: true } });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} data-testid="home-hero" className="relative -mt-[76px] min-h-[100dvh] flex flex-col justify-end pt-[128px] pb-10 overflow-hidden">
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="absolute -right-[12vw] -top-[14vw] h-[70vw] w-[70vw] max-h-[1100px] max-w-[1100px] rounded-full blur-[20px]" style={{ background: 'radial-gradient(circle at 40% 45%, rgba(108,92,231,.38), rgba(75,159,255,.14) 40%, transparent 68%)' }} />
        <div className="absolute -left-[16vw] -bottom-[24vw] h-[46vw] w-[46vw] rounded-full" style={{ background: 'radial-gradient(circle, rgba(75,159,255,.16), transparent 65%)' }} />
      </div>
      <div className={`relative ${WRAP} w-full`}>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.08fr)_minmax(0,1fr)] gap-10 lg:gap-12 items-start hero-grid">
          <div className="hero-in relative">
            <div data-hero-fade className="nd-mono flex items-center gap-4 mb-[clamp(24px,4vh,40px)]">
              <span>00</span><span className="h-px w-12 bg-white/15" />
              <span>Web &amp; App Development Company · Serving the United States</span>
            </div>
            <h1 ref={h1} className="font-display font-semibold text-[clamp(40px,4.6vw,88px)] leading-[0.95] tracking-[-0.05em] [word-spacing:.07em]">
              {HEADLINE.map((w, i) => (
                <React.Fragment key={w}>
                  <span className={MASK}><span className="inline-block">{w}</span></span>{' '}
                  {(i === 3 || i === 5) && <br className="max-sm:hidden" />}
                </React.Fragment>
              ))}
              <span className={MASK}><span className="inline-block nd-accent">inevitable.</span></span>
            </h1>
            <div data-hero-fade className="mt-[clamp(28px,5vh,52px)]">
              <p className="text-[clamp(16px,1.2vw,18px)] leading-relaxed text-[#C9C9D3] max-w-[50ch] mb-8">
                A twelve-person studio in India building websites, web apps, mobile apps, custom software, ecommerce stores and AI solutions for businesses across the United States. We work small, ship often, work your hours, and stay uncomfortably close to the work.
              </p>
              <div className="flex flex-wrap gap-3">
                <MagneticButton href={SITE.whatsapp} external variant="primary" data-testid="hero-cta-primary">Book discovery call</MagneticButton>
                <MagneticButton
                  href="#work"
                  variant="ghost"
                  data-testid="hero-cta-secondary"
                  onClick={(e) => { const el = document.getElementById('work'); if (el) { e.preventDefault(); scrollToEl(el); } }}
                >
                  See selected work
                </MagneticButton>
              </div>
            </div>
          </div>
          <HeroScreens shots={HERO_SHOTS} className="hero-screens hidden lg:block aspect-[16/9.12] mt-[var(--h1-top,88px)] mr-[4%]" />
        </div>
        <dl data-hero-fade className="hero-in mt-[clamp(36px,6vh,64px)] grid grid-cols-2 sm:grid-cols-4 border-t border-white/[0.08]">
          {STATS.map((s, i) => (
            <div key={s.label} className={`pt-5 border-white/[0.08] ${i % 2 ? 'pl-5 border-l' : ''} ${i === 2 ? 'sm:pl-5 sm:border-l' : ''} ${i > 1 ? 'max-sm:mt-5 max-sm:border-t' : ''}`}>
              <dt className="font-display font-medium text-[clamp(30px,3.2vw,48px)] tracking-[-0.04em] leading-none tabular-nums">
                <span data-count={s.kpi}>{s.kpi}</span>
                <span className="text-[.45em] text-ink-muted font-normal tracking-normal ml-0.5">{s.suffix.trim()}</span>
              </dt>
              <dd className="mt-2.5 text-[13px] text-ink-muted leading-snug">{s.label}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function Marquee() {
  const skew = useRef(null);
  useEffect(() => {
    if (!motionOn()) return undefined;
    const to = gsap.quickTo(skew.current, 'skewX', { duration: 0.5, ease: 'power3' });
    const st = ScrollTrigger.create({ onUpdate: (s) => to(gsap.utils.clamp(-8, 8, s.getVelocity() / -300)) });
    return () => st.kill();
  }, []);
  const items = CLIENTS.map((c, i) => (
    <span key={c} className={`inline-flex items-center gap-[clamp(20px,3vw,44px)] pr-[clamp(20px,3vw,44px)] font-display font-semibold text-[clamp(44px,8vw,128px)] tracking-[-0.05em] leading-[1.05] whitespace-nowrap ${i % 2 ? 'text-transparent [-webkit-text-stroke:1px_rgba(238,238,242,.3)]' : ''}`}>
      {c} <Tri />
    </span>
  ));
  return (
    <section aria-label="Products we have shipped" className="relative py-[clamp(40px,7vw,80px)] border-y border-white/[0.08] overflow-hidden">
      <div ref={skew} className="will-change-transform">
        <div className="flex w-max">
          <div className="flex items-center shrink-0 [animation:nd-mq_40s_linear_infinite]">{items}</div>
          <div className="flex items-center shrink-0 [animation:nd-mq_40s_linear_infinite]" aria-hidden>{items}</div>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  const p = useRef(null);
  useEffect(() => {
    if (!motionOn()) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(p.current.querySelectorAll('.mw'), { opacity: 0.14 }, { opacity: 1, stagger: 0.1, ease: 'none', scrollTrigger: { trigger: p.current, start: 'top 80%', end: 'bottom 45%', scrub: true } });
    }, p);
    return () => ctx.revert();
  }, []);
  const plain = 'We don’t do everything. We do six things, with the kind of obsession that doesn’t scale'.split(' ');
  return (
    <section aria-label="Studio philosophy" className="py-[clamp(110px,16vw,230px)]">
      <div className={WRAP}>
        <p ref={p} className="font-display font-medium text-[clamp(30px,4.4vw,72px)] leading-[1.06] tracking-[-0.035em] max-w-[20ch] [word-spacing:.05em]">
          {plain.map((w, i) => <React.Fragment key={i}><span className="mw">{w}</span>{' '}</React.Fragment>)}
          <span className="mw nd-accent">— and that’s the point.</span>
        </p>
        <Reveal className="nd-mono flex flex-wrap gap-8 mt-12 pl-1"><span>Est. 2017</span><span>Serving the US</span><span>12 in-house</span></Reveal>
      </div>
    </section>
  );
}

function Services() {
  const preview = useRef(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!finePointer() || !motionOn()) return undefined;
    const pv = preview.current;
    const px = gsap.quickTo(pv, 'x', { duration: 0.6, ease: 'power3' });
    const py = gsap.quickTo(pv, 'y', { duration: 0.6, ease: 'power3' });
    const rot = gsap.quickTo(pv, 'rotation', { duration: 0.8, ease: 'power3' });
    let lastX = 0;
    const onMove = (e) => { px(e.clientX + 40); py(e.clientY); rot(gsap.utils.clamp(-8, 8, (e.clientX - lastX) * 0.4)); lastX = e.clientX; };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, []);

  useEffect(() => {
    if (!preview.current || !motionOn()) return;
    gsap.to(preview.current, active === null
      ? { opacity: 0, scale: 0.9, duration: 0.3, ease: 'power4.out', overwrite: 'auto' }
      : { opacity: 1, scale: 1, duration: 0.4, ease: 'power4.out', overwrite: 'auto' });
  }, [active]);

  return (
    <section id="services" className="relative pb-[clamp(96px,14vw,200px)]">
      <div
        ref={preview}
        aria-hidden
        className="hidden [@media(hover:hover)_and_(pointer:fine)]:block fixed left-0 top-0 z-40 -ml-[170px] -mt-[115px] h-[230px] w-[340px] rounded-[14px] overflow-hidden pointer-events-none opacity-0 scale-90 shadow-[0_40px_80px_-20px_rgba(10,10,30,.9)] ring-1 ring-white/10"
      >
        {SERVICES.map((s, i) => (
          <img key={s.slug} src={HERO_IMAGES[s.slug].src.replace('w=1400', 'w=800')} alt="" loading="lazy" referrerPolicy="no-referrer"
            className={`absolute inset-0 h-full w-full object-cover [filter:grayscale(.4)_contrast(1.05)] transition-[opacity,transform] [transition-duration:900ms] [transition-timing-function:var(--ease-out)] ${active === i ? 'opacity-100 scale-100' : 'opacity-0 scale-110'}`} />
        ))}
        <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(108,92,231,.25),rgba(10,10,15,.1)_60%)]" />
      </div>

      <div className={WRAP}>
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-[clamp(48px,7vw,96px)]">
          <SectionLabel num="01" className="lg:pt-4">What we do</SectionLabel>
          <div>
            <WordReveal as="h2" text="Six services, one opinionated studio." accent="opinionated" className={H2} />
            <Reveal className="mt-7 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <p className="text-ink-muted text-[17px] max-w-[52ch]">Each one is a discipline we practise every week, not a line item on a capabilities deck.</p>
              <Link to="/services/" className="nd-link-u text-white whitespace-nowrap">All services <span aria-hidden>→</span></Link>
            </Reveal>
          </div>
        </div>

        <ul className="border-t border-white/[0.08]" onMouseLeave={() => setActive(null)}>
          {SERVICES.map((s, i) => (
            <Reveal as="li" key={s.slug} className="border-b border-white/[0.08]">
              <Link
                to={`/services/${s.slug}/`}
                data-testid={`home-service-${s.slug}`}
                onMouseEnter={() => setActive(i)}
                className="group relative isolate grid grid-cols-[1fr_auto] lg:grid-cols-[220px_1fr_auto] gap-x-8 gap-y-4 items-center py-[clamp(24px,3vw,40px)]"
              >
                <span aria-hidden className="absolute inset-y-0 -inset-x-4 md:-inset-x-12 -z-10 origin-bottom scale-y-0 bg-[linear-gradient(90deg,rgba(108,92,231,.14),rgba(75,159,255,.04))] transition-transform [transition-duration:550ms] [transition-timing-function:var(--ease-out)] group-hover:scale-y-100" />
                <span className="nd-mono col-span-2 lg:col-span-1">{s.num} / 06</span>
                <span className="font-display font-semibold text-[clamp(28px,4.2vw,64px)] tracking-[-0.045em] leading-none transition-transform [transition-duration:550ms] [transition-timing-function:var(--ease-out)] group-hover:translate-x-4">
                  {s.title}
                </span>
                <span aria-hidden className="h-12 w-12 md:h-14 md:w-14 rounded-full border border-white/15 grid place-items-center transition-colors duration-300 group-hover:bg-white group-hover:text-ink-bg group-hover:border-white">
                  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" className="transition-transform [transition-duration:550ms] [transition-timing-function:var(--ease-out)] group-hover:-rotate-45"><path d="M2 8h12M9 3l5 5-5 5" /></svg>
                </span>
                <p className="col-span-2 lg:col-start-2 lg:col-span-1 text-ink-muted text-[15px] max-w-[60ch] lg:-mt-2">{s.long}</p>
                <span className="col-span-2 lg:col-start-2 lg:col-span-1 flex flex-wrap gap-2 lg:-mt-2">
                  {s.deliverables.map((d) => (
                    <span key={d} className="font-mono text-[11px] tracking-[.04em] text-[#C9C9D3] px-2.5 py-1.5 border border-white/[0.14] rounded-md">{d}</span>
                  ))}
                </span>
              </Link>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Work() {
  const root = useRef(null);
  const featured = WORK.slice(0, 3);

  useEffect(() => {
    if (!motionOn()) return undefined;
    // Cards stack with CSS sticky; each covered card eases back a little.
    // Transform + filter on the card only: no pinning, no clip-path.
    const mm = gsap.matchMedia();
    mm.add('(min-width: 900px)', () => {
      const cards = gsap.utils.toArray(root.current.querySelectorAll('.wc'));
      cards.forEach((c, i) => {
        if (i === cards.length - 1) return;
        gsap.to(c, { scale: 0.94, filter: 'brightness(.55)', ease: 'none', scrollTrigger: { trigger: cards[i + 1], start: 'top bottom', end: 'top 20%', scrub: true } });
      });
    });
    return () => mm.revert();
  }, []);

  return (
    <section ref={root} id="work" className="relative pb-[clamp(64px,8vw,120px)]">
      <div className={WRAP}>
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-[clamp(40px,5vw,72px)]">
          <SectionLabel num="02" className="lg:pt-4">Selected work</SectionLabel>
          <div>
            <WordReveal as="h2" text="Live products. Open them." accent="Open them." className={H2} />
            <Reveal className="mt-7 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <p className="text-ink-muted text-[17px] max-w-[52ch]">Real software with real users: payroll runs, school admissions and dinner orders, every day.</p>
              <Link to="/work/" className="nd-link-u text-white whitespace-nowrap">View all <span aria-hidden>→</span></Link>
            </Reveal>
          </div>
        </div>

        <ol>
          {featured.map((w, i) => (
            <li
              key={w.slug}
              className="wc relative min-[900px]:sticky mb-6 origin-top rounded-[22px] border border-white/[0.08] bg-ink-surface overflow-hidden shadow-[0_-30px_60px_-30px_rgba(5,5,12,.95)]"
              style={{ top: `${96 + i * 22}px` }}
            >
              <Link to={`/work/${w.slug}/`} data-cursor="View" data-testid={`home-work-${w.slug}`} className="group grid grid-cols-1 min-[900px]:grid-cols-[minmax(0,0.85fr)_minmax(0,1.4fr)] min-[900px]:min-h-[min(68vh,620px)]">
                <div className="flex flex-col justify-between gap-10 p-7 md:p-10 lg:p-12 order-2 min-[900px]:order-1">
                  <div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="nd-mono">{w.sector}</span>
                      <span className="nd-mono inline-flex items-center gap-2 !text-white"><span className="nd-live" />{w.year}</span>
                    </div>
                    <h3 className="mt-8 font-display font-semibold text-[clamp(40px,4.6vw,80px)] tracking-[-0.05em] leading-[0.92]">{w.name}</h3>
                    <p className="mt-5 text-ink-muted text-[16px] leading-relaxed max-w-[42ch]">{w.summary}</p>
                  </div>
                  <div className="flex items-end justify-between gap-6 border-t border-white/[0.08] pt-6">
                    <div>
                      <b className="block font-display font-medium text-[clamp(40px,4vw,64px)] tracking-[-0.05em] leading-none tabular-nums">{w.stat.kpi}</b>
                      <span className="mt-2 block text-[13px] text-ink-muted">{w.stat.label}</span>
                    </div>
                    <span aria-hidden className="h-14 w-14 shrink-0 rounded-full border border-white/15 grid place-items-center transition-colors duration-300 group-hover:bg-white group-hover:text-ink-bg group-hover:border-white">
                      <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" className="transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:-rotate-45"><path d="M2 8h12M9 3l5 5-5 5" /></svg>
                    </span>
                  </div>
                </div>
                <div className="relative order-1 min-[900px]:order-2 aspect-[16/10] min-[900px]:aspect-auto overflow-hidden bg-[#0E0E15] min-[900px]:m-3 min-[900px]:ml-0 min-[900px]:rounded-[14px]">
                  <img
                    className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-1000 [transition-timing-function:var(--ease-out)] group-hover:scale-[1.04]"
                    src={WORK_IMAGES[w.slug]?.src}
                    alt={WORK_IMAGES[w.slug]?.alt || w.name}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_55%,rgba(10,10,15,.55))]" />
                  <span className="nd-mono absolute left-4 bottom-4 !text-[11px] !text-white">Case {String(i + 1).padStart(2, '0')} / 0{featured.length}</span>
                </div>
              </Link>
            </li>
          ))}
        </ol>

        <Reveal className="mt-14 md:mt-20 flex flex-col md:flex-row md:items-end md:justify-between gap-8 border-t border-white/[0.08] pt-10">
          <h3 className="font-display font-semibold text-[clamp(32px,4vw,64px)] leading-[0.96] tracking-[-0.045em] max-w-[16ch]">There’s more where <span className="nd-accent">these came from.</span></h3>
          <MagneticButton to="/work/" variant="ghost">View all work</MagneticButton>
        </Reveal>
      </div>
    </section>
  );
}

function Studio() {
  const root = useRef(null);
  useEffect(() => {
    if (!motionOn()) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo('.st-img', { yPercent: -6 }, { yPercent: 6, ease: 'none', scrollTrigger: { trigger: '.st-media', start: 'top bottom', end: 'bottom top', scrub: true } });
      const cards = gsap.utils.toArray('.pr');
      const mm = gsap.matchMedia();
      mm.add('(min-width: 641px)', () => {
        cards.forEach((c, i) => {
          if (i === cards.length - 1) return;
          gsap.to(c, { scale: 0.94, filter: 'brightness(.6)', ease: 'none', scrollTrigger: { trigger: cards[i + 1], start: 'top 85%', end: 'top 30%', scrub: true } });
        });
      });
      root.current.querySelectorAll('[data-count]').forEach((el) => {
        const to = parseFloat(el.dataset.count); const o = { v: 0 };
        ScrollTrigger.create({ trigger: el, start: 'top 85%', once: true, onEnter: () => gsap.to(o, { v: to, duration: 1.4, ease: 'power3.out', onUpdate: () => { el.textContent = Math.round(o.v); } }) });
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} id="studio" className="relative py-[clamp(96px,14vw,200px)]">
      <div className={WRAP}>
        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-[clamp(48px,7vw,96px)]">
          <SectionLabel num="03" className="lg:pt-4">How we work</SectionLabel>
          <WordReveal as="h2" text="Small team, big surface area." accent="big surface area." className={H2} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[clamp(40px,6vw,120px)] items-start">
          <ClipReveal className="st-media relative lg:sticky lg:top-28 rounded-[18px] overflow-hidden aspect-[16/11] lg:aspect-[4/5] ring-1 ring-white/[0.08]">
            <img className="st-img absolute -inset-y-[10%] inset-x-0 h-[120%] w-full object-cover [filter:grayscale(.9)_contrast(1.05)_brightness(.8)]" src={STUDIO_IMAGE.src} alt={STUDIO_IMAGE.alt} loading="lazy" referrerPolicy="no-referrer" />
            <div className="absolute inset-0 bg-[linear-gradient(200deg,rgba(108,92,231,.35),transparent_50%),linear-gradient(0deg,rgba(10,10,15,.75),transparent_45%)]" />
            <div className="nd-mono absolute left-6 right-6 bottom-5 flex justify-between"><span className="text-white">The studio</span><span>India</span></div>
          </ClipReveal>
          <div>
            <Reveal>
              <p className="font-display font-medium text-[clamp(24px,2.4vw,36px)] leading-[1.2] tracking-[-0.03em] mb-14">
                We embed with founders and product owners for <span className="nd-grad-text">twelve focused weeks</span>—not twelve scattered months. Designers and engineers work in the same file, on the same Slack, against the same brief. The work is the deliverable; the deck is a side-effect.
              </p>
            </Reveal>
            <ol className="mb-14">
              {PRINCIPLES.map((p, i) => (
                <li key={p.title} className="pr relative sm:sticky mb-4 rounded-[18px] border border-white/[0.08] bg-ink-surface p-6 sm:p-8 origin-top shadow-[0_-20px_40px_-20px_rgba(5,5,12,.9)]" style={{ top: `${110 + i * 20}px` }}>
                  <div className="flex items-baseline justify-between mb-10">
                    <span data-count={p.n} className="font-display font-medium text-[clamp(64px,7vw,112px)] tracking-[-0.06em] leading-[0.85] tabular-nums">{p.n}</span>
                    <span className="nd-mono">{p.unit}</span>
                  </div>
                  <h3 className="font-display font-semibold text-[22px] tracking-[-0.02em] mb-2">{p.title}</h3>
                  <p className="text-ink-muted text-[15px] max-w-[44ch]">{p.body}</p>
                </li>
              ))}
            </ol>
            <Link to="/about/" className="nd-link-u text-white">Meet the studio <span aria-hidden>→</span></Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <Manifesto />
      <Services />
      <Work />
      <Studio />
      <CityStrip num="04" />
      <CTABand />
    </>
  );
}
